/** Stop intent, exact offline identifiers and optimistic projection. */
import { useCallback, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import { addCachedRunProposalStop, addOfflineDraftRunStop, addRunStop, isOfflineDraftRun,
  removeCachedRunProposalStop, removeOfflineDraftRunStop, removeRunStop,
  reorderCachedRunProposalStops, reorderOfflineDraftRunStops, reorderRunStops,
  repairOrderReservation } from '@/api/runs';
import { useAuth } from '@/context/AuthContext';
import { offlineRunPlan } from '@/utils/offlineRunPlan';

const useStopManagement = ({ commands, selectedRunId, selectedRun, setRuns,
  captureSelection, isSelectionCurrent, isScopeCurrent, getRun,
  offline = false, runCacheLastUpdated = null }) => {
  const localDraft = offline && isOfflineDraftRun(selectedRun);
  const cached = offline && !isOfflineDraftRun(selectedRun);
  const { user } = useAuth();
  const isAdmin = Boolean(user?.roles?.includes('admin'));
  const [pendingReservationRepair, setPendingReservationRepair] = useState(null);
  const repairRef = useRef(null);
  const reorders = useRef(new Map());
  const repairIsCurrent = useCallback(target => Boolean(target && !offline && isAdmin
    && isScopeCurrent() && isSelectionCurrent(target.selection)),
  [offline, isAdmin, isScopeCurrent, isSelectionCurrent]);

  const handleAddToRun = useCallback(async orderId => {
    if (!selectedRunId || !selectedRun) return false;
    if (selectedRun.status !== 'draft') { toast.error('Cannot add orders to a committed run'); return false; }
    const selection = captureSelection();
    const result = await commands.execute({ kind: 'add', runId: selectedRunId, orderId,
      ...(offline ? { runPlan: offlineRunPlan(selectedRun) } : {}) }, {
      send: config => localDraft ? addOfflineDraftRunStop(selectedRunId, orderId, config)
        : cached ? addCachedRunProposalStop(selectedRunId, orderId, runCacheLastUpdated, config)
          : addRunStop(selectedRunId, orderId, config),
      message: 'Order added to run', refresh: 'detail',
      onRefused: error => {
        // Only a recognized refusal reaches here; generic stale-write 409 stays unknown.
        if (!offline && isAdmin && error?.response?.status === 409 && isSelectionCurrent(selection)) {
          const repair = { orderId, detail: error.response.data.detail, selection, runId: selectedRunId };
          repairRef.current = repair; setPendingReservationRepair(repair); return true;
        }
        return false;
      },
    });
    return result.accepted;
  }, [commands, selectedRunId, selectedRun, captureSelection, localDraft, cached, runCacheLastUpdated, offline, isAdmin, isSelectionCurrent]);

  const cancelReservationRepair = useCallback(() => {
    repairRef.current = null; setPendingReservationRepair(null);
  }, []);
  const confirmReservationRepair = useCallback(async () => {
    const target = pendingReservationRepair;
    if (repairRef.current !== target || !repairIsCurrent(target)
      || commands.public.blockedRun(target.runId) || commands.public.blockedOrder(target.orderId)) return false;
    repairRef.current = null; setPendingReservationRepair(null);
    const result = await commands.execute({ kind: 'repair', runId: target.runId, orderId: target.orderId, source: 'cloud' }, {
      send: config => repairOrderReservation(target.orderId, config),
      message: 'Reservation cleared. Try adding the order again.', refresh: 'detail', orders: true,
    });
    return result.accepted;
  }, [commands, pendingReservationRepair, repairIsCurrent]);

  const handleRemoveFromRun = useCallback(async orderId => {
    if (!selectedRunId || !selectedRun) return false;
    const stop = selectedRun.populated_stops?.find(item => item.order_id === orderId);
    const stopId = stop?.local_stop_id || stop?.id;
    if (localDraft && !stopId) { toast.error('Offline stop is unavailable'); return false; }
    const result = await commands.execute({ kind: 'remove', runId: selectedRunId, orderId, stopId,
      ...(offline ? { runPlan: offlineRunPlan(selectedRun) } : {}) }, {
      send: config => localDraft ? removeOfflineDraftRunStop(selectedRunId, stopId, config)
        : cached ? removeCachedRunProposalStop(selectedRunId, orderId, config) : removeRunStop(selectedRunId, orderId, config),
      message: 'Order removed from run', refresh: 'detail', orders: selectedRun.status !== 'draft',
    });
    return result.accepted;
  }, [commands, selectedRunId, selectedRun, localDraft, cached, offline]);

  const handleDragEnd = useCallback(async ({ active, over }) => {
    if (!over || active.id === over.id || !selectedRun?.populated_stops) return false;
    if (selectedRun.status !== 'draft') { toast.error('Cannot reorder a committed run'); return false; }
    const stops = selectedRun.populated_stops;
    const from = stops.findIndex(stop => stop.order_id === active.id);
    const to = stops.findIndex(stop => stop.order_id === over.id);
    if (from < 0 || to < 0) return false;
    const updatedStops = arrayMove(stops, from, to).map((stop, index) => ({ ...stop, sequence: index + 1 }));
    const orderIds = updatedStops.map(stop => stop.order_id);
    const stopIds = updatedStops.map(stop => stop.local_stop_id || stop.id);
    if (localDraft && stopIds.some(id => !id)) { toast.error('Offline stop order is unavailable'); return false; }
    const edit = {};
    let optimisticRun;
    const result = await commands.execute({ kind: 'reorder', runId: selectedRunId,
      ...(offline ? { runPlan: offlineRunPlan(selectedRun) } : {}),
      ...(localDraft ? { stopIds } : { orderIds }) }, {
      onStart: () => {
        reorders.current.set(selectedRunId, edit);
        setRuns(previous => previous.map(run => run.id === selectedRunId ? { ...run, populated_stops: updatedStops } : run));
        optimisticRun = getRun(selectedRunId);
      },
      send: config => localDraft ? reorderOfflineDraftRunStops(selectedRunId, stopIds, config)
        : cached ? reorderCachedRunProposalStops(selectedRunId, orderIds, runCacheLastUpdated, config)
          : reorderRunStops(selectedRunId, orderIds, config),
      message: 'Run stops reordered', refresh: 'detail',
      onAccepted: data => {
        if (reorders.current.get(selectedRunId) === edit && getRun(selectedRunId)?.populated_stops === updatedStops) {
          setRuns(previous => previous.map(run => run.id === selectedRunId ? { ...run, ...(offline ? data : {}) } : run));
        }
      },
      onRefused: () => {
        if (reorders.current.get(selectedRunId) === edit && getRun(selectedRunId) === optimisticRun) {
          setRuns(previous => previous.map(run => run.id === selectedRunId ? { ...run, populated_stops: stops } : run));
        }
        return false;
      },
    });
    if (reorders.current.get(selectedRunId) === edit) reorders.current.delete(selectedRunId);
    return result.accepted;
  }, [commands, selectedRunId, selectedRun, localDraft, cached, runCacheLastUpdated, setRuns, getRun, offline]);

  return { handleAddToRun, handleRemoveFromRun, handleDragEnd,
    pendingReservationRepair: repairIsCurrent(pendingReservationRepair) ? pendingReservationRepair : null,
    confirmReservationRepair, cancelReservationRepair };
};
export default useStopManagement;
